import { Fragment, useState, useEffect, useMemo } from 'react';
import { FiActivity, FiUsers, FiDollarSign, FiTrendingUp, FiArrowUp, FiArrowDown } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { SparkLineChart } from '@mui/x-charts/SparkLineChart';
import Sidebar from '../../components/sidebar/Sidebar';
import { Money, Username, useMask } from '../../utils/privacyMask';
import { getPackagePrice, getPackageProfit } from '../../utils/packages';
import './Homepage.css';
import '../analysis/AnalysisPage.css';

/* ── Constants ──────────────────────────────────────────────────────── */
const PAYMENT_META = {
  paypal:  { icon: 'bi-paypal',           label: 'PayPal',      color: '#60a5fa', bar: '#3b82f6' },
  crypto:  { icon: 'bi-currency-bitcoin', label: 'Crypto',      color: '#fbbf24', bar: '#f59e0b' },
  card:    { icon: 'bi-credit-card',      label: 'Card',        color: '#34d399', bar: '#10b981' },
  other:   { icon: 'bi-three-dots',       label: 'Other',       color: '#94a3b8', bar: 'var(--color-muted)' },
  unknown: { icon: 'bi-dash-circle',      label: 'Unspecified', color: 'var(--color-muted)', bar: 'var(--color-muted)' },
};

const STAT_THEMES = [
  { accent: '#3b82f6', glow: 'rgba(59,130,246,0.28)',  subtle: 'rgba(59,130,246,0.1)'  },
  { accent: '#f59e0b', glow: 'rgba(245,158,11,0.28)',  subtle: 'rgba(245,158,11,0.1)'  },
  { accent: '#10b981', glow: 'rgba(16,185,129,0.28)',  subtle: 'rgba(16,185,129,0.1)'  },
  { accent: '#06b6d4', glow: 'rgba(6,182,212,0.28)',   subtle: 'rgba(6,182,212,0.1)'   },
  { accent: '#a78bfa', glow: 'rgba(167,139,250,0.28)', subtle: 'rgba(167,139,250,0.1)' },
  { accent: '#f472b6', glow: 'rgba(244,114,182,0.28)', subtle: 'rgba(244,114,182,0.1)' },
];

/* ── Sub-components ─────────────────────────────────────────────────── */
function StatCard({ icon, label, value, change, changeFormat, loading, theme, sparkline }) {
  const isFlat = change === 0;
  const isPositive = change > 0;
  const format = changeFormat || (c => `${c > 0 ? '+' : ''}${c.toFixed(1)}%`);
  return (
    <div
      className="stat-card dash-fade-in h-100"
      style={{ '--accent': theme.accent, '--accent-glow': theme.glow, '--accent-subtle': theme.subtle }}
    >
      <div className="d-flex justify-content-between align-items-start gap-3">
        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          <div className="stat-label">{label}</div>
          {loading
            ? <div className="skel mt-1" style={{ width: '55%', height: 32 }} />
            : <div className="stat-value">{value}</div>
          }
        </div>
        <div className="stat-icon">
          {icon}
        </div>
      </div>
      {!loading && change !== null && (
        <div className={`stat-change ${isFlat ? 'text-muted' : isPositive ? 'text-success' : 'text-danger'}`}>
          {!isFlat && (isPositive ? <FiArrowUp size={12} /> : <FiArrowDown size={12} />)}
          {isFlat ? 'No change' : format(change)} vs last month
        </div>
      )}
      {!loading && sparkline && sparkline.length > 1 && (
        <div className="stat-sparkline">
          <SparkLineChart
            data={sparkline}
            height={32}
            colors={[theme.accent]}
            showTooltip
            showHighlight
          />
        </div>
      )}
    </div>
  );
}

function SkeletonRows({ cols, rows = 5 }) {
  return [...Array(rows)].map((_, i) => (
    <tr key={i}>
      {[...Array(cols)].map((_, j) => (
        <td key={j}><span className="skel" style={{ width: `${55 + Math.random() * 35}%`, height: 14, display: 'block' }} /></td>
      ))}
    </tr>
  ));
}

/* ── Main component ─────────────────────────────────────────────────── */
const SHORT_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function monthKey(d) { return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; }
function fmtMoney(n) { return `€${(n||0).toFixed(2)}`; }

const MINI_CHART_SX = {
  '& .MuiChartsAxis-line':      { stroke: 'var(--color-border)' },
  '& .MuiChartsAxis-tick':      { stroke: 'var(--color-border)' },
  '& .MuiChartsAxis-tickLabel': { fill: 'var(--color-muted)' },
  '& .MuiChartsLegend-label':   { fill: 'var(--color-muted) !important' },
  background: 'transparent',
};

export default function Homepage() {
  const { mask } = useMask();

  const [accountsMap, setAccountsMap]         = useState({});
  const [loading, setLoading]                 = useState(true);
  const [activeAccounts, setActiveAccounts]   = useState([]);
  const [accountsGained, setAccountsGained]   = useState([]);
  const [accountsLost, setAccountsLost]       = useState([]);
  const [accountsWithSubsMonthly, setAccountsWithSubsMonthly] = useState([]);
  const [paymentBreakdown, setPaymentBreakdown] = useState({});
  const [subscriptions, setSubscriptions]     = useState([]);
  const [analysisOpen, setAnalysisOpen]       = useState(false);

  const [metrics, setMetrics] = useState({
    activeSubscriptions: 0,
    monthlyRevenue:      0,
    monthlyProfit:       0,
    monthlyProfitAdjusted: 0,
    revenueComparison:   0,
    profitComparison:    0,
    recentTransactions:  [],
    monthlyData:         [],
    totalAccounts:       0,
    activeAccountsCount: 0,
    resellerRevenue:         0,
    resellerProfit:          0,
    resellerRevenuePrev:     0,
    resellerProfitPrev:      0,
    resellerPurchases:       0,
    resellerCreditsSold:     0,
    pendingMediaRequests: 0,
    accountsWithSubsCurrentCount:  0,
    accountsWithSubsPreviousCount: 0,
    accountsWithSubsNetChange:     0,
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

        const [rawRes, accRes, subRes, pendingReqRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/raw`,                  { headers }),
          axios.get(`${process.env.REACT_APP_API_URL}/account`,              { headers }),
          axios.get(`${process.env.REACT_APP_API_URL}/subscription?all=true`, { headers }),
          axios.get(`${process.env.REACT_APP_API_URL}/botdata/requests?status=pending`, { headers }).catch(() => ({ data: [] })),
        ]);

        if (!alive) return;

        setMetrics({
          monthlyProfit:       rawRes.data.totals.currentMonthlyProfit,
          // Falls back to currentMonthlyProfit until the backend's expense-split (Part 4b) lands —
          // Adjusted == Profit when there are no logged expenses, which is correct either way.
          monthlyProfitAdjusted: rawRes.data.totals.currentMonthlyProfitAdjusted ?? rawRes.data.totals.currentMonthlyProfit,
          monthlyRevenue:      rawRes.data.totals.currentMonthlyRevenue,
          revenueComparison:   rawRes.data.totals.revenueComparison.percentage,
          profitComparison:    rawRes.data.totals.profitComparison.percentage,
          activeSubscriptions: rawRes.data.totals.activeSubscriptions,
          recentTransactions:  rawRes.data.recentTransactions,
          monthlyData:         rawRes.data.monthlyData,
          totalAccounts:       rawRes.data.totals.totalAccounts,
          pendingMediaRequests: Array.isArray(pendingReqRes.data) ? pendingReqRes.data.length : 0,
          activeAccountsCount: rawRes.data.totals.activeAccountsCount || 0,
          resellerRevenue:     rawRes.data.totals.currentMonthlyResellerRevenue   || 0,
          resellerProfit:      rawRes.data.totals.currentMonthlyResellerProfit    || 0,
          resellerRevenuePrev: rawRes.data.totals.previousMonthlyResellerRevenue  || 0,
          resellerProfitPrev:  rawRes.data.totals.previousMonthlyResellerProfit   || 0,
          resellerPurchases:   rawRes.data.totals.currentMonthlyResellerPurchases || 0,
          resellerCreditsSold: rawRes.data.totals.currentMonthlyResellerCredits   || 0,
          accountsWithSubsCurrentCount:  rawRes.data.totals.accountsWithSubsComparison?.currentCount ?? 0,
          accountsWithSubsPreviousCount: rawRes.data.totals.accountsWithSubsComparison?.previousCount ?? 0,
          accountsWithSubsNetChange:     rawRes.data.totals.accountsWithSubsComparison?.netChange ?? 0,
        });

        setActiveAccounts(rawRes.data.activeAccounts || []);
        setAccountsGained(rawRes.data.gainedAccounts || []);
        setAccountsLost(rawRes.data.lostAccounts || []);
        setAccountsWithSubsMonthly(rawRes.data.accountsWithSubsMonthly || []);
        setPaymentBreakdown(rawRes.data.paymentMethodBreakdown || {});
        setSubscriptions(subRes.data || []);

        const map = {};
        accRes.data.forEach(a => { map[a._id] = a.username; });
        setAccountsMap(map);
        setLoading(false);
      } catch (err) {
        console.error('Dashboard fetch error:', err);
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const paymentList = Object.entries(paymentBreakdown).sort((a, b) => b[1].revenue - a[1].revenue);
  const totalPmtRev = paymentList.reduce((s, [, d]) => s + d.revenue, 0);

  // ── Revenue / profit targets (beat last month) ─────────────────────────
  const MONTH_NAMES_FULL = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const parseMonthStr = str => {
    if (!str) return 0;
    const [mn, yr] = str.split(' ');
    const idx = MONTH_NAMES_FULL.indexOf(mn);
    return idx === -1 ? 0 : new Date(Number(yr), idx, 1).getTime();
  };
  const sortedMonthly = useMemo(
    () => [...metrics.monthlyData].sort((a, b) => parseMonthStr(b.month) - parseMonthStr(a.month)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [metrics.monthlyData]
  );
  // Chronological (oldest → newest) trend for sparklines, last 6 months
  const chronoMonthly = useMemo(() => [...sortedMonthly].reverse().slice(-6), [sortedMonthly]);
  const revenueSpark = useMemo(() => chronoMonthly.map(m => m.revenue || 0), [chronoMonthly]);
  const profitSpark  = useMemo(() => chronoMonthly.map(m => m.profit  || 0), [chronoMonthly]);

  const prevMonthData   = sortedMonthly[1] || null;
  const revTarget       = prevMonthData?.revenue || 0;
  const profTarget      = prevMonthData?.profit  || 0;
  const revCurrent      = metrics.monthlyRevenue;
  const profCurrent     = metrics.monthlyProfit;
  const revPct          = revTarget  > 0 ? Math.min((revCurrent  / revTarget)  * 100, 200) : 0;
  const profPct         = profTarget > 0 ? Math.min((profCurrent / profTarget) * 100, 200) : 0;
  const barColor = pct => pct >= 100 ? '#10b981' : pct >= 75 ? '#f59e0b' : '#ef4444';
  const barWidth = pct => `${Math.min(pct, 100)}%`;

  // ── Reseller split: the month with and without resellers ──────────────
  // Credit sales sit inside monthlyRevenue/monthlyProfit already (they are real
  // sales, not expenses), so customers is the remainder rather than its own total.
  const split = useMemo(() => {
    const rows = [
      { label: 'Revenue', icon: 'bi-cash-stack', accent: '#34d399',
        total: metrics.monthlyRevenue, reseller: metrics.resellerRevenue, prev: metrics.resellerRevenuePrev },
      { label: 'Profit',  icon: 'bi-graph-up-arrow', accent: '#38bdf8',
        total: metrics.monthlyProfit,  reseller: metrics.resellerProfit,  prev: metrics.resellerProfitPrev },
    ];
    return rows.map(r => ({
      ...r,
      customers: r.total - r.reseller,
      // Only meaningful as a share of a positive total: in a loss-making month a
      // positive reseller profit over a negative total reads as a negative share.
      share: r.total > 0 ? (r.reseller / r.total) * 100 : null,
      change: r.prev !== 0 ? ((r.reseller - r.prev) / Math.abs(r.prev)) * 100 : null,
    }));
  }, [metrics.monthlyRevenue, metrics.monthlyProfit, metrics.resellerRevenue,
      metrics.resellerProfit, metrics.resellerRevenuePrev, metrics.resellerProfitPrev]);

  const hasResellerActivity = metrics.resellerPurchases > 0 || metrics.resellerRevenue !== 0;

  // ── Mini analysis: next 6 months from today ───────────────────────────
  const miniMonths = useMemo(() => {
    const now = new Date();
    const map = {};
    subscriptions.forEach(sub => {
      const d   = new Date(Number(sub.expireDate));
      const key = monthKey(d);
      if (!map[key]) {
        map[key] = {
          key,
          label:      `${SHORT_MONTHS[d.getMonth()]} '${String(d.getFullYear()).slice(-2)}`,
          predictedRevenue: 0,
          predictedProfit:  0,
        };
      }
      map[key].predictedRevenue += getPackagePrice(sub.package)  || 0;
      map[key].predictedProfit  += getPackageProfit(sub.package) || 0;
    });
    // build 6 consecutive months starting from current month
    return Array.from({ length: 6 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const k = monthKey(d);
      return map[k] || {
        key: k,
        label: `${SHORT_MONTHS[d.getMonth()]} '${String(d.getFullYear()).slice(-2)}`,
        predictedRevenue: 0,
        predictedProfit:  0,
      };
    });
  }, [subscriptions]);

  return (
    <>
      <Sidebar title="Dashboard" />

      <div className="dash-page py-4 px-3 px-md-4">

        {/* ── Stat cards ── */}
        <div className="row g-3 mb-4">
          {[
            { icon: <FiActivity size={20} color={STAT_THEMES[0].accent} />, label: 'Active Subscriptions', value: metrics.activeSubscriptions, change: null, theme: STAT_THEMES[0] },
            { icon: <FiUsers    size={20} color={STAT_THEMES[1].accent} />, label: 'Active Accounts',      value: `${metrics.activeAccountsCount} / ${metrics.totalAccounts}`, change: metrics.accountsWithSubsNetChange, changeFormat: c => `${c > 0 ? '+' : ''}${c} account${Math.abs(c) === 1 ? '' : 's'}`, theme: STAT_THEMES[1] },
            { icon: <FiDollarSign size={20} color={STAT_THEMES[2].accent} />, label: 'Monthly Revenue',    value: mask(`€${metrics.monthlyRevenue.toLocaleString()}`, 'money'), change: metrics.revenueComparison, theme: STAT_THEMES[2], sparkline: revenueSpark },
            { icon: <FiTrendingUp size={20} color={STAT_THEMES[3].accent} />, label: 'Monthly Profit',     value: mask(`€${metrics.monthlyProfit.toLocaleString()}`, 'money'),  change: metrics.profitComparison,  theme: STAT_THEMES[3], sparkline: profitSpark },
            { icon: <FiTrendingUp size={20} color={STAT_THEMES[4].accent} />, label: 'Profit Adjusted',    value: mask(`€${metrics.monthlyProfitAdjusted.toLocaleString()}`, 'money'), change: null, theme: STAT_THEMES[4], sparkline: profitSpark },
            { icon: <FiActivity size={20} color={STAT_THEMES[5].accent} />, label: 'Pending Media Requests', value: metrics.pendingMediaRequests, change: null, theme: STAT_THEMES[5] },
          ].map((s, i) => (
            <div key={i} className="col-12 col-sm-6 col-lg-4 col-xl-2 dash-fade-in" style={{ animationDelay: `${i * 0.07}s` }}>
              <StatCard {...s} loading={loading} />
            </div>
          ))}
        </div>

        {/* ── Reseller split ── */}
        {!loading && (
          <div className="row g-3 mb-4">
            <div className="col-12">
              <div className="panel-card">
                <div className="panel-header">
                  <h6 className="panel-title">
                    <i className="bi bi-person-badge-fill" style={{ color: '#a78bfa' }}></i>
                    Revenue &amp; Profit — reseller split
                  </h6>
                  <span style={{ fontSize: '0.72rem', color: 'var(--color-subtle)' }}>This month</span>
                </div>

                {!hasResellerActivity ? (
                  <div style={{ padding: '18px 0 6px', color: 'var(--color-muted)', fontSize: '0.85rem' }}>
                    <i className="bi bi-moon-stars me-2" />
                    No reseller activity this month — every figure above is customer business.
                  </div>
                ) : (
                  <>
                    <div className="reseller-split">
                      <div className="reseller-split-head" />
                      <div className="reseller-split-head">Total</div>
                      <div className="reseller-split-head">Customers</div>
                      <div className="reseller-split-head">Resellers</div>

                      {split.map(row => (
                        <Fragment key={row.label}>
                          <div className="reseller-split-label">
                            <i className={`bi ${row.icon} me-2`} style={{ color: row.accent }} />
                            {row.label}
                          </div>
                          <div className="reseller-split-cell total">
                            {mask(fmtMoney(row.total), 'money')}
                          </div>
                          <div className="reseller-split-cell">
                            {mask(fmtMoney(row.customers), 'money')}
                          </div>
                          <div className="reseller-split-cell" style={{ color: '#c4b5fd' }}>
                            {mask(fmtMoney(row.reseller), 'money')}
                            {row.share !== null && (
                              <span className="reseller-split-share">{row.share.toFixed(1)}%</span>
                            )}
                          </div>
                        </Fragment>
                      ))}
                    </div>

                    <div className="reseller-split-foot">
                      {metrics.resellerPurchases} credit purchase{metrics.resellerPurchases === 1 ? '' : 's'}
                      {' · '}
                      {mask(metrics.resellerCreditsSold.toLocaleString(), 'number')} credits sold
                      {split[0].change !== null && (
                        <>
                          {' · reseller revenue '}
                          <span style={{ color: split[0].change >= 0 ? '#34d399' : '#f87171', fontWeight: 600 }}>
                            {split[0].change >= 0 ? '+' : ''}{split[0].change.toFixed(1)}%
                          </span>
                          {' vs last month'}
                        </>
                      )}
                      {' · '}
                      <Link to="/resellers" style={{ color: '#c4b5fd', textDecoration: 'none', fontWeight: 600 }}>
                        View resellers <i className="bi bi-arrow-right" />
                      </Link>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Monthly targets ── */}
        {!loading && prevMonthData && (
          <div className="row g-3 mb-4">
            <div className="col-12">
              <div className="panel-card">
                <div className="panel-header">
                  <h6 className="panel-title">
                    <i className="bi bi-trophy-fill" style={{ color: '#f59e0b' }}></i>
                    Monthly Targets
                  </h6>
                  <span style={{ fontSize: '0.72rem', color: 'var(--color-subtle)' }}>
                    Target: beat {prevMonthData.month}
                  </span>
                </div>
                <div className="target-row">
                  <div className="target-header">
                    <span className="target-label"><i className="bi bi-cash-stack me-1" style={{ color: '#34d399' }} />Revenue</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span style={{ fontSize: '0.72rem', color: 'var(--color-muted)' }}>
                        {mask(`€${revCurrent.toLocaleString()} / €${revTarget.toLocaleString()}`, 'money')}
                      </span>
                      <span className="target-pct" style={{ color: barColor(revPct) }}>
                        {revPct >= 100 ? '✓ ' : ''}{revPct.toFixed(0)}%
                      </span>
                    </div>
                  </div>
                  <div className="target-bar-track">
                    <div className="target-bar-fill" style={{ width: barWidth(revPct), background: barColor(revPct) }} />
                  </div>
                  <span className="target-sub">
                    {mask(revPct >= 100
                      ? `+€${(revCurrent - revTarget).toFixed(2)} ahead of target 🎉`
                      : `€${(revTarget - revCurrent).toFixed(2)} remaining to beat last month`, 'money')}
                  </span>
                </div>
                <div className="target-row">
                  <div className="target-header">
                    <span className="target-label"><i className="bi bi-graph-up-arrow me-1" style={{ color: '#38bdf8' }} />Profit</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span style={{ fontSize: '0.72rem', color: 'var(--color-muted)' }}>
                        {mask(`€${profCurrent.toLocaleString()} / €${profTarget.toLocaleString()}`, 'money')}
                      </span>
                      <span className="target-pct" style={{ color: barColor(profPct) }}>
                        {profPct >= 100 ? '✓ ' : ''}{profPct.toFixed(0)}%
                      </span>
                    </div>
                  </div>
                  <div className="target-bar-track">
                    <div className="target-bar-fill" style={{ width: barWidth(profPct), background: barColor(profPct) }} />
                  </div>
                  <span className="target-sub">
                    {mask(profPct >= 100
                      ? `+€${(profCurrent - profTarget).toFixed(2)} ahead of target 🎉`
                      : `€${(profTarget - profCurrent).toFixed(2)} remaining to beat last month`, 'money')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Active accounts + Payment methods ── */}
        <div className="row g-3 mb-4">

          {/* Active accounts */}
          <div className="col-12 col-lg-6">
            <div className="panel-card h-100">
              <div className="panel-header">
                <h6 className="panel-title">
                  <i className="bi bi-people-fill" style={{ color: '#f59e0b' }}></i>
                  Live Active Accounts
                </h6>
                <span className="count-pill">
                  {loading ? '…' : metrics.activeAccountsCount} active
                </span>
              </div>
              <div className="dash-scroll" style={{ maxHeight: 320 }}>
                <table className="dash-table">
                  <thead>
                    <tr>
                      <th>Username</th>
                      <th>Email</th>
                      <th className="text-end">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <SkeletonRows cols={3} rows={5} />
                    ) : activeAccounts.length === 0 ? (
                      <tr><td colSpan={3} className="text-center py-5" style={{ color: 'var(--color-muted)' }}>
                        <i className="bi bi-inbox me-2"></i>No active accounts
                      </td></tr>
                    ) : (
                      activeAccounts.map(acc => (
                        <tr key={acc._id}>
                          <td style={{ fontWeight: 600, color: 'var(--color-text)' }}><Username value={acc.username} /></td>
                          <td style={{ color: 'var(--color-muted)', fontSize: '0.82rem' }}>{acc.email || '—'}</td>
                          <td className="text-end">
                            <span className="badge-active">
                              <span className="pulse-dot"></span>Active
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Payment methods */}
          <div className="col-12 col-lg-6">
            <div className="panel-card h-100">
              <div className="panel-header">
                <h6 className="panel-title">
                  <i className="bi bi-credit-card-fill" style={{ color: '#06b6d4' }}></i>
                  Payment Methods
                </h6>
              </div>

              {loading ? (
                <div className="p-4">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="mb-4">
                      <div className="d-flex justify-content-between mb-2">
                        <span className="skel" style={{ width: 90, height: 14 }} />
                        <span className="skel" style={{ width: 60, height: 14 }} />
                      </div>
                      <div className="skel" style={{ width: '100%', height: 5 }} />
                    </div>
                  ))}
                </div>
              ) : paymentList.length === 0 ? (
                <div className="text-center py-5" style={{ color: 'var(--color-muted)' }}>
                  <i className="bi bi-inbox me-2"></i>No transaction data
                </div>
              ) : (
                paymentList.map(([method, data]) => {
                  const meta = PAYMENT_META[method] || PAYMENT_META.unknown;
                  const pct  = totalPmtRev > 0 ? (data.revenue / totalPmtRev) * 100 : 0;
                  return (
                    <div key={method} className="payment-row">
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <div className="payment-label" style={{ color: meta.color }}>
                            <i className={`bi ${meta.icon}`}></i>
                            {meta.label}
                          </div>
                          <div className="payment-meta">Profit: {mask(`€${data.profit.toFixed(2)}`, 'money')}</div>
                        </div>
                        <div>
                          <div className="payment-amount" style={{ color: 'var(--color-text)' }}>{mask(`€${data.revenue.toFixed(2)}`, 'money')}</div>
                          <div className="payment-count">{data.count} txn{data.count !== 1 ? 's' : ''}</div>
                        </div>
                      </div>
                      <div className="dash-progress">
                        <div className="dash-progress-bar" style={{ width: `${pct}%`, background: meta.bar }}></div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* ── Account activity (this month vs last) ── */}
        {!loading && (accountsGained.length > 0 || accountsLost.length > 0 || accountsWithSubsMonthly.length > 0) && (
          <div className="row g-3 mb-4">
            <div className="col-12">
              <div className="panel-card">
                <div className="panel-header">
                  <h6 className="panel-title">
                    <i className="bi bi-arrow-left-right" style={{ color: '#a78bfa' }}></i>
                    Account Activity — This Month vs Last
                  </h6>
                  <span style={{ fontSize: '0.72rem', color: 'var(--color-subtle)' }}>
                    {metrics.accountsWithSubsCurrentCount} / {metrics.accountsWithSubsPreviousCount} accounts with a subscription
                  </span>
                </div>
                {accountsWithSubsMonthly.length > 1 && (
                  <div className="p-3 pb-0">
                    <LineChart
                      xAxis={[{
                        data: accountsWithSubsMonthly.map(m => m.month),
                        scaleType: 'point',
                        tickLabelStyle: { fill: 'var(--color-muted)', fontSize: 11 },
                      }]}
                      yAxis={[{ tickLabelStyle: { fill: 'var(--color-muted)', fontSize: 11 } }]}
                      series={[{
                        data: accountsWithSubsMonthly.map(m => m.count),
                        label: 'Accounts with a subscription',
                        color: '#a78bfa',
                        curve: 'monotoneX',
                        area: true,
                        valueFormatter: v => `${v} account${v === 1 ? '' : 's'}`,
                      }]}
                      height={220}
                      margin={{ left: 40, right: 16, top: 16, bottom: 32 }}
                      sx={MINI_CHART_SX}
                      slotProps={{ legend: { hidden: true } }}
                    />
                  </div>
                )}
                <div className="row g-3 p-3">
                  <div className="col-12 col-md-6">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <FiArrowUp size={14} color="#34d399" />
                      <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#34d399' }}>Gained ({accountsGained.length})</span>
                    </div>
                    {accountsGained.length === 0 ? (
                      <div style={{ fontSize: '0.8rem', color: 'var(--color-muted)' }}>None</div>
                    ) : (
                      <div className="d-flex flex-wrap gap-2">
                        {accountsGained.map(a => (
                          <span key={a._id} style={{ display: 'inline-flex', alignItems: 'center', background: 'rgba(52,211,153,0.12)', color: '#34d399', border: '1px solid rgba(52,211,153,0.28)', borderRadius: 999, padding: '3px 10px', fontSize: '0.72rem', fontWeight: 600 }}>
                            <Username value={a.username} />
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <FiArrowDown size={14} color="#f87171" />
                      <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#f87171' }}>Lost ({accountsLost.length})</span>
                    </div>
                    {accountsLost.length === 0 ? (
                      <div style={{ fontSize: '0.8rem', color: 'var(--color-muted)' }}>None</div>
                    ) : (
                      <div className="d-flex flex-wrap gap-2">
                        {accountsLost.map(a => (
                          <span key={a._id} style={{ display: 'inline-flex', alignItems: 'center', background: 'rgba(248,113,113,0.12)', color: '#f87171', border: '1px solid rgba(248,113,113,0.28)', borderRadius: 999, padding: '3px 10px', fontSize: '0.72rem', fontWeight: 600 }}>
                            <Username value={a.username} />
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Recent transactions + Monthly stats ── */}
        <div className="row g-3">

          {/* Recent transactions */}
          <div className="col-12 col-lg-8">
            <div className="panel-card">
              <div className="panel-header">
                <h6 className="panel-title">
                  <i className="bi bi-clock-history" style={{ color: '#3b82f6' }}></i>
                  Recent Transactions
                </h6>
              </div>
              <div className="table-responsive">
                <table className="dash-table">
                  <thead>
                    <tr>
                      <th>Account</th>
                      <th>Revenue</th>
                      <th>Profit</th>
                      <th>Method</th>
                      <th>Notes</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <SkeletonRows cols={6} rows={5} />
                    ) : metrics.recentTransactions.length === 0 ? (
                      <tr><td colSpan={6} className="text-center py-5" style={{ color: 'var(--color-muted)' }}>
                        <i className="bi bi-inbox me-2"></i>No transactions yet
                      </td></tr>
                    ) : (
                      metrics.recentTransactions.map((tx, i) => {
                        const meta = PAYMENT_META[tx.paymentMethod] || null;
                        return (
                          <tr key={i}>
                            <td style={{ fontWeight: 600, color: 'var(--color-text)' }}><Username value={accountsMap[tx.accountID] || '—'} /></td>
                            <td style={{ fontWeight: 700, color: '#34d399' }}>{mask(`€${tx.revenue.toFixed(2)}`, 'money')}</td>
                            <td style={{ color: '#38bdf8' }}>{mask(`€${tx.profit.toFixed(2)}`, 'money')}</td>
                            <td>
                              {meta ? (
                                <span className={`method-pill ${tx.paymentMethod}`}>
                                  <i className={`bi ${meta.icon}`}></i>
                                  {meta.label}
                                </span>
                              ) : <span style={{ color: 'var(--color-muted)' }}>—</span>}
                            </td>
                            <td style={{ color: 'var(--color-muted)', fontSize: '0.82rem', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {tx.notes || '—'}
                            </td>
                            <td style={{ color: 'var(--color-muted)', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                              {new Date(Number(tx.transactionDate)).toLocaleDateString()}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Monthly stats */}
          <div className="col-12 col-lg-4">
            <div className="panel-card">
              <div className="panel-header">
                <h6 className="panel-title">
                  <i className="bi bi-bar-chart-fill" style={{ color: '#10b981' }}></i>
                  Monthly Stats
                </h6>
              </div>
              <div className="dash-scroll" style={{ maxHeight: 340 }}>
                <table className="dash-table">
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>Revenue</th>
                      <th>Profit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <SkeletonRows cols={3} rows={6} />
                    ) : metrics.monthlyData.length === 0 ? (
                      <tr><td colSpan={3} className="text-center py-4" style={{ color: 'var(--color-muted)' }}>No data</td></tr>
                    ) : (
                      metrics.monthlyData.map((m, i) => (
                        <tr key={m.month} className={i === 0 ? 'current-month-row' : ''}>
                          <td style={{ fontWeight: i === 0 ? 700 : 400 }}>{m.month}</td>
                          <td style={{ color: '#34d399', fontWeight: 600 }}>{mask(`€${m.revenue.toFixed(2)}`, 'money')}</td>
                          <td style={{ color: '#38bdf8' }}>{mask(`€${m.profit.toFixed(2)}`, 'money')}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

        </div>

        {/* ── Mini analysis widget ── */}
        <div className="panel-card mt-3">
          <div className="panel-header" style={{ cursor: 'pointer' }} onClick={() => setAnalysisOpen(o => !o)}>
            <h6 className="panel-title">
              <i className="bi bi-graph-up-arrow" style={{ color: '#a78bfa' }}></i>
              Revenue Forecast
            </h6>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Link
                to="/analysis"
                style={{ fontSize: '0.75rem', color: 'var(--color-muted)', textDecoration: 'none' }}
                onClick={e => e.stopPropagation()}
              >
                Full analysis →
              </Link>
              <i
                className={`bi bi-chevron-down mini-analysis-chevron ${analysisOpen ? 'open' : ''}`}
                style={{ fontSize: '0.8rem', color: 'var(--color-muted)', transition: 'transform 0.2s ease', transform: analysisOpen ? 'rotate(180deg)' : 'none' }}
              />
            </div>
          </div>

          {analysisOpen && (
            <div style={{ padding: '16px 8px 8px' }}>
              {loading ? (
                <div style={{ padding: '32px', textAlign: 'center', color: 'var(--color-muted)' }}>Loading…</div>
              ) : (
                <>
                  <BarChart
                    xAxis={[{
                      data: miniMonths.map(m => m.label),
                      scaleType: 'band',
                      tickLabelStyle: { fill: 'var(--color-muted)', fontSize: 11 },
                      disableLine: true,
                      disableTicks: true,
                    }]}
                    yAxis={[{
                      tickLabelStyle: { fill: 'var(--color-muted)', fontSize: 11 },
                      disableLine: true,
                      disableTicks: true,
                    }]}
                    series={[
                      { data: miniMonths.map(m => parseFloat(m.predictedRevenue.toFixed(2))), label: 'Pred. Revenue', color: '#3b82f6', valueFormatter: v => mask(`€${v}`, 'money') },
                      { data: miniMonths.map(m => parseFloat(m.predictedProfit.toFixed(2))),  label: 'Pred. Profit',  color: '#06b6d4', valueFormatter: v => mask(`€${v}`, 'money') },
                    ]}
                    height={200}
                    margin={{ left: 65, right: 16, top: 36, bottom: 48 }}
                    sx={MINI_CHART_SX}
                    slotProps={{
                      legend: {
                        direction: 'row',
                        position: { vertical: 'top', horizontal: 'right' },
                        itemMarkWidth: 8,
                        itemMarkHeight: 8,
                        labelStyle: { fill: 'var(--color-muted)', fontSize: 11 },
                      },
                    }}
                  />
                  <table className="dash-table" style={{ marginTop: 8 }}>
                    <thead>
                      <tr>
                        <th>Month</th>
                        <th>Pred. Revenue</th>
                        <th>Pred. Profit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {miniMonths.map((m, i) => (
                        <tr key={m.key} className={i === 0 ? 'current-month-row' : ''}>
                          <td style={{ fontWeight: i === 0 ? 700 : 400 }}>{m.label}</td>
                          <td style={{ color: '#60a5fa', fontWeight: 600 }}><Money value={m.predictedRevenue} formatter={fmtMoney} /></td>
                          <td style={{ color: '#22d3ee' }}><Money value={m.predictedProfit} formatter={fmtMoney} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          )}
        </div>

      </div>
    </>
  );
}
